export interface ProductVariants {
  schema_version: 'amazon-product-variants/v1'; source_path: 'variations'; coverage: 'source_list_not_verified_complete';
  state: 'observed' | 'unavailable'; observed_count: number | null; omitted_count: number | null; rejected_count: number | null;
  items: { source_index: number; asin: string | null; name: string | null; price: string | null; currency: string | null }[];
}
export interface ProductInventoryClaim {
  schema_version: 'amazon-inventory-claim/v1'; semantics: 'page_claim_not_verified_inventory';
  source_path: 'availability'; source_text: string; quantity: number;
}
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const exact = (value: unknown, keys: string[]): value is Record<string, unknown> => object(value)
  && Object.keys(value).length === keys.length && keys.every(key => Object.prototype.hasOwnProperty.call(value, key));
const integer = (value: unknown, min: number, max: number) => Number.isSafeInteger(value) && Number(value) >= min && Number(value) <= max;
const nullablePattern = (value: unknown, pattern: RegExp) => value === null || typeof value === 'string' && pattern.exec(value)?.[0] === value;
const decimal = /^(?:0|[1-9]\d{0,15})(?:\.\d{0,11}[1-9])?$/;

/** Shared read boundary for latest products and pinned report citations. Never infer missing values. */
export function productAttributes(data: Record<string, unknown>): { variants?: ProductVariants; inventory?: ProductInventoryClaim } {
  const variants = data.variant_observation, inventory = data.inventory_claim;
  const invalid = () => { throw new Error('Invalid observed product attributes'); };
  if (variants !== undefined) {
    if (!exact(variants, ['schema_version','source_path','coverage','state','observed_count','omitted_count','rejected_count','items'])
      || variants.schema_version !== 'amazon-product-variants/v1' || variants.source_path !== 'variations'
      || variants.coverage !== 'source_list_not_verified_complete' || !Array.isArray(variants.items) || variants.items.length > 50) invalid();
    const v = variants as Record<string, unknown>, items = v.items as unknown[];
    if (v.state === 'unavailable') {
      if (v.observed_count !== null || v.omitted_count !== null || v.rejected_count !== null || items.length !== 0) invalid();
    } else {
      const inspected = Math.min(Number(v.observed_count), 50);
      if (v.state !== 'observed' || !integer(v.observed_count, 0, 50_000) || !integer(v.omitted_count, 0, 50_000)
        || !integer(v.rejected_count, 0, 50) || v.omitted_count !== Number(v.observed_count) - inspected
        || Number(v.rejected_count) + items.length !== inspected) invalid();
      let last = -1;
      for (const item of items) {
        if (!exact(item, ['source_index','asin','name','price','currency']) || !integer(item.source_index, last + 1, inspected - 1)
          || !nullablePattern(item.asin, /^[A-Z0-9]{10}$/) || !nullablePattern(item.currency, /^[A-Z]{3}$/)
          || !nullablePattern(item.price, decimal) || !(item.name === null || typeof item.name === 'string'
            && item.name.trim().length > 0 && item.name.length <= 256 && !/[\x00-\x1f\x7f]/.test(item.name))
          || item.asin === null && item.name === null) invalid();
        last = Number((item as Record<string, unknown>).source_index);
      }
    }
  }
  if (inventory !== undefined) {
    if (!exact(inventory, ['schema_version','semantics','source_path','source_text','quantity'])
      || inventory.schema_version !== 'amazon-inventory-claim/v1' || inventory.semantics !== 'page_claim_not_verified_inventory'
      || inventory.source_path !== 'availability' || typeof inventory.source_text !== 'string'
      || inventory.source_text !== data.availability || !integer(inventory.quantity, 1, 999_999_999)) invalid();
    const i = inventory as Record<string, unknown>;
    const match = /^Only ([1-9]\d{0,8}) left in stock(?:\.| - order soon\.)?$/i.exec(String(i.source_text));
    if (!match || match[0] !== i.source_text || Number(match[1]) !== i.quantity) invalid();
  }
  return { variants: variants as ProductVariants | undefined, inventory: inventory as ProductInventoryClaim | undefined };
}
